import { useContext, useMemo } from 'react'
import moment from 'moment'
import usePublishSalesCopilotStay from 'components/OfferPage/SalesCopilot/usePublishSalesCopilotStay'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import CurrencyContext from 'contexts/currencyContext'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCheckoutDate, getIsFlightsSelected, getPropertyCheapestRates } from 'selectors/offerPage/offerPageSelectors'

interface Props {
  property: App.Property.Property
  offerId: string
  offerType: string
}

// Publishes the cheapest rate for the agent's dates on LPC and bedbank property pages.
// Deposit, reserve-for-$0 and offer-end data only exist on the offer, which SPP code must not read, so they need backend support first.
function SalesCopilotPropertyBridge({ property, offerId, offerType }: Props) {
  const offerPageState = useContext(OfferPageStateContext)
  const currentCurrency = useContext(CurrencyContext)
  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)
  const [cheapestRate, cheapestRateWithFlights] = useAppSelector(state => getPropertyCheapestRates(offerPageState, state))

  const checkIn = offerPageState.checkInDate
  const checkOut = getCheckoutDate(offerPageState)
  const flightsSelected = getIsFlightsSelected(offerPageState)

  const offerStay = useMemo<App.SalesCopilotOfferStay | undefined>(() => {
    // Before dates are chosen the cheapest rate is an "anytime" price with no stay behind it.
    const rate = cheapestRate && 'service' in cheapestRate ? cheapestRate : undefined
    if (!checkIn || !checkOut || !rate) return undefined

    const hotelTotal = rate.totals.displayPrice
    const total = flightsSelected && cheapestRateWithFlights ? cheapestRateWithFlights.totals.displayPrice : hotelTotal
    const memberDiscount = canViewLuxPlusBenefits && rate.luxPlusTotals ? hotelTotal - rate.luxPlusTotals.displayPrice : 0
    const hotelMargin = rate.totals.marginAud !== undefined ? rate.totals.marginAud - memberDiscount : undefined

    return {
      offerId,
      offerType,
      offerName: property.name,
      checkIn,
      checkOut,
      nights: moment(checkOut).diff(moment(checkIn), 'days'),
      currencyCode: currentCurrency,
      total,
      hotelTotal,
      hotelMargin,
      flightsSelected,
      depositAvailable: false,
      depositPercentage: 0,
      depositDueDays: 0,
      reserveForZeroAvailable: false,
      availableRooms: rate.availableRooms,
      bookedLast72Hours: property.stats.booked,
      valuedAt: rate.totals.valuedUpToPrice,
      discountPercent: rate.totals.discount,
    }
  }, [property, offerId, offerType, checkIn, checkOut, cheapestRate, cheapestRateWithFlights, flightsSelected, currentCurrency, canViewLuxPlusBenefits])

  usePublishSalesCopilotStay(offerStay)
  return null
}

export default SalesCopilotPropertyBridge
